import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { createContext, useContext, useState } from "react";
import { Icon, IconProps } from "../Icon";
import { MenuItems, MenuItemProps } from "./MenuItems";
import { MobileDrawer } from "../MobileDrawer/MobileDrawer";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";

type ContextMenuProps = {
  menuItems: MenuItemProps[];
  trigger?: React.ReactNode;
  triggerClassName?: string;
  triggerIconProps?: IconProps;
  triggerTestId?: string;
  contentTestId?: string;
  leadChildren?: React.ReactNode;
  tailChildren?: React.ReactNode;
  side?: ContextMenuSideAlignment;
  sideOffset?: number;
  align?: ContextMenuAlignAlignment;
  alignOffset?: number;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => boolean | void;
  noBorder?: boolean;
};
export enum ContextMenuSideAlignment {
  RIGHT = "right",
  LEFT = "left",
  BOTTOM = "bottom",
  TOP = "top",
}

export enum ContextMenuAlignAlignment {
  CENTER = "center",
  START = "start",
  END = "end",
}

const ContextMenuCloseContext = createContext<() => void>(() => undefined);

export const useContextMenuClose = () => useContext(ContextMenuCloseContext);

export const ContextMenu = ({
  menuItems,
  trigger,
  triggerClassName,
  triggerIconProps = { icon: "icon-lt-more-2-line", size: "lg", extraClasses: "text-neutral-800", cursor: "pointer" },
  triggerTestId,
  contentTestId,
  leadChildren,
  tailChildren,
  side = ContextMenuSideAlignment.BOTTOM,
  sideOffset = 5,
  align = ContextMenuAlignAlignment.END,
  alignOffset = 0,
  disabled = false,
  onOpenChange,
  noBorder = false,
}: ContextMenuProps) => {
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  const isMobile = useIsMobile();

  return (
    <div data-testid="context-menu">
      {leadChildren}
      <DropdownMenu.Root
        open={open}
        onOpenChange={(opn) => {
          const result = onOpenChange?.(opn) as boolean | void | undefined;
          // If onOpenChange returns false, prevent the state change
          if (result === false) {
            return;
          }
          setOpen(opn);
        }}
      >
        {trigger ? (
          <DropdownMenu.Trigger asChild className={triggerClassName} data-testid={triggerTestId} disabled={disabled}>
            {trigger}
          </DropdownMenu.Trigger>
        ) : (
          <DropdownMenu.Trigger
            className={clsx(
              triggerClassName,
              "inline-flex items-center justify-center rounded-md",
              "h-10 px-4 py-2",
              "disabled:pointer-events-none disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
              "bg-transparent hover:bg-accent hover:text-accent-foreground",
              "ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              open ? "bg-blue-100" : ""
            )}
            data-testid={triggerTestId}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
          >
            <Icon {...triggerIconProps} />
          </DropdownMenu.Trigger>
        )}

        {isMobile ? (
          <MobileDrawer open={open} onOpenChange={setOpen}>
            <div className="flex flex-col">
              <MenuItems items={menuItems} setMobileDrawer={setOpen} noBorder={noBorder} />
            </div>
          </MobileDrawer>
        ) : (
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              side={side}
              sideOffset={sideOffset}
              align={align}
              alignOffset={alignOffset}
              className="z-50 flex flex-col py-1 overflow-hidden bg-white border rounded-lg shadow-md w-60 border-blue-grey-200"
              data-testid={contentTestId}
            >
              <ContextMenuCloseContext.Provider value={handleClose}>
                <MenuItems items={menuItems} noBorder={noBorder} />
              </ContextMenuCloseContext.Provider>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        )}
      </DropdownMenu.Root>
      {tailChildren}
    </div>
  );
};
